import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Badge color configuration
 * Unified color scheme for episode workflow statuses
 *
 * Color mapping:
 * - secondary (draft/archived): Gray (#6b7280)
 * - processing (generating): Amber (#f59e0b)
 * - available (scripts pending review): Orange (#f97316)
 * - approved (scripts approved): Cyan (#00c8b4)
 * - ready (audio ready/approved): Teal (#14b8a6)
 * - merged: Blue (#3b82f6)
 * - published: Green (#22c55e)
 * - destructive (failed): Red
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "bg-gray-500/60 text-[var(--pm-text-primary)] border-gray-500",
        destructive:
          "bg-red-500/60 text-white border-red-500",
        outline:
          "text-foreground",
        processing:
          "bg-amber-500/60 text-white border-amber-500",
        queued:
          "bg-blue-500/60 text-white border-blue-500",
        available:
          "bg-orange-500/60 text-white border-orange-500",
        approved:
          "bg-[#00c8b4]/60 text-white border-[#00c8b4]",
        ready:
          "bg-teal-500/60 text-white border-teal-500",
        merged:
          "bg-blue-500/60 text-white border-blue-500",
        published:
          "bg-green-500/60 text-white border-green-500",
        stepnum:
          "bg-gray-500/60 text-white border-gray-500",
        // Outline variants for status indicators (matching filled colors)
        "outline-success":
          "bg-transparent text-green-500 border-green-500",
        "outline-warning":
          "bg-transparent text-amber-500 border-amber-500",
        "outline-ready":
          "bg-transparent text-teal-500 border-teal-500",
        "outline-destructive":
          "bg-transparent text-red-500 border-red-500",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
